/**
 * Shot Shape Preview — SVG overhead dispersion overlay.
 *
 * Shows the expected shot shape and miss pattern from an overhead view,
 * based on face angle, club path, spin axis, and handicap variance.
 *
 * Renders:
 *  - Center line: expected ball flight curve (draw/fade/straight)
 *  - Dispersion cone: 1σ and 2σ miss boundaries based on handicap
 *  - Landing zone ellipse: where the ball is most likely to end up
 *
 * The overlay is a DOM SVG element positioned in the mobile UI.
 */

import { getVarianceProfile } from '../simulation/SwingVariance';
import { deriveSpinAxisFromImpact, deriveLaunchDirection } from '../simulation/SwingInput';

export interface ShotShapeParams {
  faceAngle: number;      // degrees (+ = open)
  clubPath: number;       // degrees (+ = in-to-out)
  launchAngle: number;    // degrees
  attackAngle: number;    // degrees
  ballSpeed: number;      // mph
  spinRate: number;       // rpm
  handicap: number;       // 0-25
}

// SVG viewport dimensions (relative units)
const SVG_W = 200;
const SVG_H = 320;
const ORIGIN_X = SVG_W / 2;  // tee position centered
const ORIGIN_Y = SVG_H - 30; // tee near bottom

// Scale: 1 yard ≈ 1.2 SVG units for a ~250yd range fitting in viewport
const YARD_SCALE = 1.1;

/**
 * Create or get the SVG overlay container element.
 */
export function getOrCreateSvgOverlay(): SVGSVGElement {
  let svg = document.getElementById('shot-shape-svg') as unknown as SVGSVGElement;
  if (svg) return svg;

  svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('id', 'shot-shape-svg');
  svg.setAttribute('viewBox', `0 0 ${SVG_W} ${SVG_H}`);
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.style.cssText = `
    width: 100%; max-width: 280px; height: auto; aspect-ratio: ${SVG_W}/${SVG_H};
    margin: 0 auto; display: block;
  `;

  return svg;
}

/**
 * Estimate carry distance from ball speed (rough model for preview only).
 */
function estimateCarry(ballSpeedMph: number, launchAngle: number, spinRate: number): number {
  // Simplified carry model: distance ≈ f(ball speed, launch, spin)
  const base = ballSpeedMph * 1.75; // rough yards
  const launchOpt = 12; // optimal launch for distance
  const launchPenalty = Math.abs(launchAngle - launchOpt) * 1.5;
  const spinOpt = 2400;
  const spinPenalty = Math.abs(spinRate - spinOpt) * 0.005;
  return Math.max(50, base - launchPenalty - spinPenalty);
}

/**
 * Compute lateral curve at a given fraction of flight (0=tee, 1=landing).
 * Returns yards of lateral movement. Positive = right.
 */
function lateralCurve(
  spinAxis: number,
  carryYards: number,
  fraction: number,
): number {
  // Spin axis tilt creates lateral curve. The curve accelerates later in flight.
  // Total lateral ≈ carry * sin(spinAxis°) * 0.4 (empirical scaling)
  const totalLateral = carryYards * Math.sin(spinAxis * Math.PI / 180) * 0.4;
  // Quadratic curve — most movement in 2nd half of flight
  return totalLateral * fraction * fraction;
}

/**
 * Build the SVG content for the shot shape preview.
 */
export function renderShotShape(params: ShotShapeParams): SVGSVGElement {
  const svg = getOrCreateSvgOverlay();

  const {
    faceAngle, clubPath, launchAngle, attackAngle,
    ballSpeed, spinRate, handicap,
  } = params;

  // Derive shot shape parameters
  const spinAxis = deriveSpinAxisFromImpact(faceAngle, clubPath, launchAngle, attackAngle);
  const launchDir = deriveLaunchDirection(faceAngle, clubPath, launchAngle, attackAngle);
  const carry = estimateCarry(ballSpeed, launchAngle, spinRate);
  const variance = getVarianceProfile(handicap);

  // Dispersion widths (1σ in yards at landing)
  const lateralSigma = carry * Math.sin(variance.launchDirStdDev * Math.PI / 180) +
                        carry * Math.sin(variance.spinAxisStdDev * Math.PI / 180) * 0.3;
  const depthSigma = carry * 0.03 + handicap * 0.4; // depth variance increases with handicap

  // Build SVG content
  const parts: string[] = [];

  // Background
  parts.push(`<rect width="${SVG_W}" height="${SVG_H}" fill="#0a1a0a" rx="12"/>`);

  // Yard markers
  for (let yd = 50; yd <= 300; yd += 50) {
    const y = ORIGIN_Y - yd * YARD_SCALE;
    if (y < 10) break;
    parts.push(`<line x1="10" y1="${y}" x2="${SVG_W - 10}" y2="${y}" stroke="#1a3a1a" stroke-width="0.5"/>`);
    parts.push(`<text x="${SVG_W - 8}" y="${y - 2}" fill="#2a5a2a" font-size="7" text-anchor="end">${yd}</text>`);
  }

  // Center line (straight reference)
  parts.push(`<line x1="${ORIGIN_X}" y1="${ORIGIN_Y}" x2="${ORIGIN_X}" y2="10" stroke="#1a3a1a" stroke-width="0.3" stroke-dasharray="3,3"/>`);

  // 2σ dispersion cone (faint)
  const sigma2Color = 'rgba(255,180,60,0.08)';
  const sigma1Color = 'rgba(255,180,60,0.15)';
  const sigma1W = lateralSigma * YARD_SCALE;
  const sigma2W = lateralSigma * 2 * YARD_SCALE;

  // Expected flight path — curved line from tee to landing
  const pathPoints: string[] = [];
  const N = 20;
  for (let i = 0; i <= N; i++) {
    const frac = i / N;
    const dist = carry * frac;
    const lat = lateralCurve(spinAxis, carry, frac) + launchDir * dist * 0.017;
    const px = ORIGIN_X + lat * YARD_SCALE;
    const py = ORIGIN_Y - dist * YARD_SCALE;
    pathPoints.push(`${px.toFixed(1)},${py.toFixed(1)}`);
  }

  // Landing point
  const landX = parseFloat(pathPoints[pathPoints.length - 1].split(',')[0]);
  const landY2 = parseFloat(pathPoints[pathPoints.length - 1].split(',')[1]);

  // 2σ dispersion ellipse at landing
  parts.push(`<ellipse cx="${landX}" cy="${landY2}" rx="${sigma2W}" ry="${depthSigma * 2 * YARD_SCALE}" fill="${sigma2Color}" stroke="rgba(255,180,60,0.12)" stroke-width="0.5"/>`);
  // 1σ dispersion ellipse at landing
  parts.push(`<ellipse cx="${landX}" cy="${landY2}" rx="${sigma1W}" ry="${depthSigma * YARD_SCALE}" fill="${sigma1Color}" stroke="rgba(255,180,60,0.25)" stroke-width="0.5"/>`);

  // Dispersion cone from tee to landing (2σ boundary lines)
  parts.push(`<line x1="${ORIGIN_X}" y1="${ORIGIN_Y}" x2="${landX - sigma2W}" y2="${landY2}" stroke="rgba(255,180,60,0.1)" stroke-width="0.5"/>`);
  parts.push(`<line x1="${ORIGIN_X}" y1="${ORIGIN_Y}" x2="${landX + sigma2W}" y2="${landY2}" stroke="rgba(255,180,60,0.1)" stroke-width="0.5"/>`);

  // Flight path curve
  const pathColor = spinAxis > 2 ? '#ff8844' : spinAxis < -2 ? '#4488ff' : '#44ff88';
  parts.push(`<polyline points="${pathPoints.join(' ')}" fill="none" stroke="${pathColor}" stroke-width="1.5" stroke-linecap="round"/>`);

  // Landing dot
  parts.push(`<circle cx="${landX}" cy="${landY2}" r="3" fill="${pathColor}" opacity="0.9"/>`);

  // Tee marker
  parts.push(`<circle cx="${ORIGIN_X}" cy="${ORIGIN_Y}" r="2.5" fill="#fff" opacity="0.6"/>`);

  // Shot shape label
  const shapeLabel = spinAxis > 5 ? 'SLICE' :
                     spinAxis > 2 ? 'FADE' :
                     spinAxis < -5 ? 'HOOK' :
                     spinAxis < -2 ? 'DRAW' : 'STRAIGHT';
  const labelColor = spinAxis > 2 ? '#ff8844' : spinAxis < -2 ? '#4488ff' : '#44ff88';
  parts.push(`<text x="${SVG_W / 2}" y="16" fill="${labelColor}" font-size="9" font-weight="600" text-anchor="middle" font-family="system-ui">${shapeLabel}</text>`);

  // Carry distance label
  parts.push(`<text x="${SVG_W / 2}" y="${SVG_H - 8}" fill="#667" font-size="7" text-anchor="middle" font-family="system-ui">~${Math.round(carry)} YD CARRY</text>`);

  // Dispersion label
  const dispYds = Math.round(lateralSigma * 2);
  parts.push(`<text x="${SVG_W / 2}" y="28" fill="#556" font-size="7" text-anchor="middle" font-family="system-ui">±${dispYds}yd dispersion</text>`);

  svg.innerHTML = parts.join('\n');
  return svg;
}

